import { Injectable } from '@angular/core';
import { HttpService } from 'app/services/http-service';
import { map, Observable } from 'rxjs';
import { z } from 'zod';
import * as schema from './incremental-progress-schema';

const pathSchemas = {
	'v2/schedule_editor/create_progress': { request: schema.createProgressRequestSchema, response: schema.createProgressResponseSchema },
	'v2/schedule_editor/list_progress': { request: schema.listProgressRequestSchema, response: schema.listProgressResponseSchema },
	'v2/schedule_editor/update_progress': { request: schema.updateProgressRequestSchema, response: schema.updateProgressResponseSchema },
} as const;

type Paths = typeof pathSchemas;

@Injectable({
	providedIn: 'root',
})
export class IncrementalProgressService {
	constructor(private http: HttpService) {}

	/**
	 * Helper for sending a post request and parsing the expected response. The returned observable errors if the response
	 * doesn't match the expected schema.
	 *
	 * All response schemas are expected to have a top-level `progress` key.
	 */
	private doRequest<Path extends keyof Paths>(
		path: Path,
		body: z.infer<Paths[Path]['request']>
	): Observable<z.infer<Paths[Path]['response']>['progress']> {
		return this.http.post<unknown>(path, body, undefined, false).pipe(map((resp) => pathSchemas[path].response.parse(resp).progress));
	}

	/**
	 * Returns an observable that calls the create_progress endpoint on subscribe.
	 *
	 * The observable errors if the response doesn't match the expected schema.
	 */
	createProgress(progress?: schema.ScheduleEditorData) {
		return this.doRequest('v2/schedule_editor/create_progress', { data: progress ?? {} });
	}

	/**
	 * Returns an observable that calls the list_progress endpoint on subscribe.
	 *
	 * The observable errors if the response doesn't match the expected schema.
	 */
	listProgress() {
		return this.doRequest('v2/schedule_editor/list_progress', {});
	}

	/**
	 * Returns an observable that calls the update_progress endpoint on subscribe.
	 *
	 * The observable errors if the response doesn't match the expected schema.
	 */
	updateProgress(id: schema.UpdateProgressRequest['id'], progress: schema.ScheduleEditorData) {
		return this.doRequest('v2/schedule_editor/update_progress', { id, data: progress });
	}
}
